/**
 * DatabaseManager - Manage Supabase database tables
 */
import {
  AlertTriangle,
  CheckCircle,
  Download,
  Loader2,
  RefreshCw,
  Table,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../services/api';

interface TableInfo {
  name: string;
  count: number;
  description: string;
}

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const DatabaseManager: React.FC = () => {
  const [tables, setTables] = useState<TableInfo[]>([
    { name: 'exams', count: 0, description: 'Concours disponibles' },
    { name: 'subjects', count: 0, description: 'Matières par concours' },
    { name: 'skills', count: 0, description: 'Compétences par matière' },
    { name: 'lessons', count: 0, description: 'Leçons d\'apprentissage' },
    { name: 'questions', count: 0, description: 'Questions/exercices' },
    { name: 'quizzes', count: 0, description: 'Quiz/chapitres' },
    { name: 'users', count: 0, description: 'Utilisateurs inscrits' },
    { name: 'user_lesson_progress', count: 0, description: 'Progression utilisateurs' },
  ]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const loadTableCounts = async () => {
    setLoading(true);
    try {
      // Appeler l'endpoint de statistiques du learning system
      const statsRes = await authFetch(`${API_BASE_URL}/api/learning/db/stats`);
      const statsData = await statsRes.json();

      // Load exams to get counts
      const examsRes = await fetch(`${API_BASE_URL}/api/exams`);
      const examsData = await examsRes.json();

      const newTables = [...tables];

      // Exams
      newTables[0].count = examsData.exams?.length || 0;

      // Calculate subjects, quizzes from exams
      let subjectsCount = 0;
      let quizzesCount = 0;

      if (examsData.exams) {
        for (const exam of examsData.exams) {
          try {
            const subjectsRes = await fetch(`${API_BASE_URL}/api/exams/${exam.id}/subjects`);
            const subjectsData = await subjectsRes.json();
            subjectsCount += subjectsData.subjects?.length || 0;

            for (const subject of subjectsData.subjects || []) {
              try {
                const quizzesRes = await fetch(`${API_BASE_URL}/api/exams/${exam.id}/subjects/${subject.id}/quizzes`);
                const quizzesData = await quizzesRes.json();
                quizzesCount += quizzesData.quizzes?.length || 0;
              } catch (e) {
                // Ignore
              }
            }
          } catch (e) {
            // Ignore
          }
        }
      }

      // Update counts from learning stats if available
      if (statsData.success && statsData.stats) {
        newTables[1].count = subjectsCount;  // subjects
        newTables[2].count = statsData.stats.total_skills || 0;  // skills
        newTables[3].count = statsData.stats.total_lessons || 0;  // lessons
        newTables[4].count = statsData.stats.total_exercises || 0;  // questions
        newTables[5].count = quizzesCount;  // quizzes
        // users et user_lesson_progress nécessitent un endpoint spécifique
        newTables[6].count = 0;  // users - à implémenter
        newTables[7].count = 0;  // user_lesson_progress - à implémenter
      } else {
        newTables[1].count = subjectsCount;
        newTables[5].count = quizzesCount;
      }

      setTables(newTables);
    } catch (err: any) {
      setMessage({ type: 'error', text: `Erreur: ${err.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTableCounts();
  }, []);

  const handleClearTable = async (tableName: string) => {
    if (confirmDelete !== tableName) {
      setConfirmDelete(tableName);
      return;
    }

    setActionLoading(tableName);
    setMessage(null);

    try {
      // Call API to clear table
      const response = await authFetch(`${API_BASE_URL}/api/admin/clear-table/${tableName}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setMessage({ type: 'success', text: `Table ${tableName} vidée avec succès` });
        await loadTableCounts();
      } else {
        // If endpoint doesn't exist yet, show info
        setMessage({
          type: 'error',
          text: `Endpoint non disponible. Utilisez SQL: TRUNCATE TABLE ${tableName} CASCADE;`
        });
      }
    } catch (err: any) {
      setMessage({
        type: 'error',
        text: `Utilisez Supabase SQL Editor: TRUNCATE TABLE ${tableName} CASCADE;`
      });
    } finally {
      setActionLoading(null);
      setConfirmDelete(null);
    }
  };

  const handleExportTable = async (tableName: string) => {
    setActionLoading(`export-${tableName}`);
    try {
      // For now, just show a message
      setMessage({
        type: 'success',
        text: `Export de ${tableName} - Utilisez Supabase Dashboard > Table Editor > Export`
      });
    } finally {
      setActionLoading(null);
    }
  };

  const sqlCommands = {
    clearAll: `-- Vider toutes les tables (sauf utilisateurs)
TRUNCATE TABLE questions CASCADE;
TRUNCATE TABLE quizzes CASCADE;
TRUNCATE TABLE subjects CASCADE;
TRUNCATE TABLE exams CASCADE;`,
    clearQuestions: `-- Vider uniquement les questions
DELETE FROM questions;`,
    clearLearning: `-- Vider les tables d'apprentissage
DELETE FROM questions WHERE lesson_id IS NOT NULL;
DELETE FROM user_lesson_progress;
DELETE FROM lessons;
DELETE FROM skills;`,
    viewCounts: `-- Voir le nombre d'enregistrements par table
SELECT 'exams' as table_name, COUNT(*) as count FROM exams
UNION ALL SELECT 'subjects', COUNT(*) FROM subjects
UNION ALL SELECT 'skills', COUNT(*) FROM skills
UNION ALL SELECT 'lessons', COUNT(*) FROM lessons
UNION ALL SELECT 'questions', COUNT(*) FROM questions
UNION ALL SELECT 'quizzes', COUNT(*) FROM quizzes
UNION ALL SELECT 'users', COUNT(*) FROM users
UNION ALL SELECT 'user_lesson_progress', COUNT(*) FROM user_lesson_progress
ORDER BY count DESC;`,
    viewLearningStructure: `-- Voir la structure d'apprentissage
SELECT 
  e.name as examen,
  s.name as matiere,
  sk.skill_name as competence,
  COUNT(DISTINCT l.id) as nb_lecons,
  SUM(l.question_count) as nb_questions
FROM exams e
JOIN subjects s ON s.exam_id = e.id
JOIN skills sk ON sk.subject_id = s.id
LEFT JOIN lessons l ON l.skill_id = sk.id
GROUP BY e.name, s.name, sk.skill_name
ORDER BY e.name, s.name;`,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Base de Données</h1>
          <p className="text-gray-500">Gestion des tables Supabase</p>
        </div>
        <button
          onClick={loadTableCounts}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Actualiser
        </button>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-start gap-3 ${message.type === 'success'
          ? 'bg-green-50 border border-green-200'
          : 'bg-red-50 border border-red-200'
          }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
          )}
          <div className="flex-1">
            <p className={message.type === 'success' ? 'text-green-700' : 'text-red-700'}>
              {message.text}
            </p>
          </div>
          <button
            onClick={() => setMessage(null)}
            className="text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>
      )}

      {/* Tables grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {tables.map((table) => (
          <div
            key={table.name}
            className="bg-white rounded-xl p-5 shadow-sm border border-gray-200"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Table className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{table.name}</h3>
                  <p className="text-sm text-gray-500">{table.description}</p>
                </div>
              </div>
              <span className="text-2xl font-bold text-gray-900">
                {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : table.count}
              </span>
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => handleExportTable(table.name)}
                disabled={actionLoading === `export-${table.name}`}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button
                onClick={() => handleClearTable(table.name)}
                disabled={actionLoading === table.name}
                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition ${confirmDelete === table.name
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
              >
                {actionLoading === table.name ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {confirmDelete === table.name ? 'Confirmer' : 'Vider'}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* SQL Commands */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-900">Commandes SQL utiles</h2>
          <p className="text-sm text-gray-500">À exécuter dans Supabase Dashboard → SQL Editor</p>
        </div>

        <div className="divide-y divide-gray-200">
          {Object.entries(sqlCommands).map(([key, sql]) => (
            <div key={key} className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">
                  {key === 'clearAll' && '🗑️ Vider toutes les tables'}
                  {key === 'clearQuestions' && '📝 Vider les questions seulement'}
                  {key === 'clearLearning' && '📚 Vider le système d\'apprentissage'}
                  {key === 'viewCounts' && '📊 Voir les comptages'}
                  {key === 'viewLearningStructure' && '🎓 Structure d\'apprentissage'}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(sql);
                    setMessage({ type: 'success', text: 'SQL copié!' });
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  Copier
                </button>
              </div>
              <pre className="bg-gray-900 text-green-400 p-3 rounded-lg text-sm overflow-x-auto">
                {sql}
              </pre>
            </div>
          ))}
        </div>
      </div>

      {/* Supabase link */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-lg mb-1">Accéder à Supabase</h3>
            <p className="text-green-100">Gérez directement vos tables et données</p>
          </div>
          <a
            href="https://supabase.com/dashboard"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-white text-green-600 rounded-lg font-medium hover:bg-green-50 transition"
          >
            Ouvrir Dashboard
          </a>
        </div>
      </div>
    </div>
  );
};

export default DatabaseManager;
